import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Wand2, Save, ChevronLeft, ChevronRight, Plus, Tag, Copy, Check, ArrowLeft, Bold, Italic, Underline, Strikethrough, List, ListOrdered, Heading1, Heading2, Quote, Code, Minus, Undo, Redo, Link, RemoveFormatting, Paperclip, Loader2, Sparkles, ChevronDown, PenLine, FileText, MessageSquare } from 'lucide-react';
import { apiUpload, apiDelete, apiPost } from '../../utils/api';
import toast from 'react-hot-toast';
import { getConfig } from '../../utils/config';
import { modelServicePost } from '../../utils/modelService';

const API_BASE = (() => {
  const url = getConfig('API_URL') || '';
  return url.replace(/\/api\/v1\/?$/, '');
})();

function resolveUrl(url) {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}
import DOMPurify from 'dompurify';
import { marked } from 'marked';

function FormatToolbar({ editorRef, isDark, noteId, onEnsureSaved, onAttachmentUploaded, onAiResult }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [showImproveMenu, setShowImproveMenu] = useState(false);
  const aiButtonRef = useRef(null);
  const aiMenuRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const savedSelectionTextRef = useRef('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Close AI menu on outside click
  useEffect(() => {
    const handle = (e) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target) &&
          aiButtonRef.current && !aiButtonRef.current.contains(e.target)) {
        setShowAiMenu(false);
        setShowImproveMenu(false);
        setShowPromptInput(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Save current selection before opening menu — capture both range and text
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
      savedSelectionTextRef.current = sel.toString() || '';
    } else {
      savedSelectionRef.current = null;
      savedSelectionTextRef.current = '';
    }
  };

  const getEditorContent = () => editorRef.current?.innerHTML || '';
  const getSelectedText = () => savedSelectionTextRef.current || '';
  const hasContent = () => {
    const text = editorRef.current?.innerText?.trim();
    return text && text.length > 0;
  };

  const handleAiAction = async (action, instruction = '') => {
    setAiLoading(true);
    setShowAiMenu(false);
    setShowImproveMenu(false);
    setShowPromptInput(false);

    try {
      let result;

      if (action === 'generate') {
        // Use the main RAG-powered /generate endpoint for full knowledge-grounded generation
        const prompt = instruction || 'Write a note';
        const response = await modelServicePost('/generate', {
          message: `Write a well-structured note in HTML format (use h2, h3, p, ul, ol, li, strong, em tags, no markdown) about: ${prompt}`,
          chat_history: [],
          reasoning_mode: 'fast',
        });
        if (!response.ok) throw new Error('AI request failed');
        const data = await response.json();
        result = data.response || data.result || '';
      } else {
        // Call model_service directly for editing operations
        const content = getEditorContent();
        const selection = getSelectedText();
        const response = await modelServicePost('/note-ai', { action, content, selection, instruction });
        if (!response.ok) throw new Error('AI request failed');
        const data = await response.json();
        result = data.result || '';
      }

      if (!result) {
        toast.error('JPRO could not generate content');
        return;
      }

      onAiResult?.(action, result, savedSelectionRef.current, savedSelectionTextRef.current);
    } catch {
      toast.error('JPRO is unavailable right now');
    } finally {
      setAiLoading(false);
    }
  };

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // If note isn't saved yet, save it first
      let id = noteId;
      if (!id && onEnsureSaved) {
        id = await onEnsureSaved();
      }
      if (!id) {
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      const response = await apiUpload(`/notes/${id}/upload_attachment`, formData);
      if (response.ok) {
        const data = await response.json();
        const att = data.attachment;
        const fullUrl = resolveUrl(att.url);

        // Insert inline at cursor position
        editorRef.current?.focus();
        const isImage = att.type?.startsWith('image/');
        if (isImage) {
          document.execCommand('insertHTML', false,
            `<img src="${fullUrl}" alt="${att.name}" style="display:inline-block;max-width:100%;border-radius:8px;margin:4px;vertical-align:top;cursor:pointer;" data-attachment-id="${att.id}" />`
          );
        } else {
          document.execCommand('insertHTML', false,
            `<a href="${fullUrl}" target="_blank" rel="noopener" data-attachment-id="${att.id}" style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:6px;background:${isDark ? '#374151' : '#f3f4f6'};color:${isDark ? '#d1d5db' : '#4b5563'};font-size:13px;text-decoration:none;margin:2px 4px;">📎 ${att.name}</a>&nbsp;`
          );
        }
        if (onAttachmentUploaded) onAttachmentUploaded(att);
      }
    } catch { /* silent */ }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const btnClass = `p-1.5 rounded transition-colors ${
    isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-800'
  }`;
  const divider = <div className={`w-px h-4 mx-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />;

  return (
    <div className={`relative z-20 flex items-center gap-0.5 px-3 md:px-4 py-1.5 border-b flex-shrink-0 overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
      <button onClick={() => exec('undo')} className={btnClass} title="Undo"><Undo size={14} /></button>
      <button onClick={() => exec('redo')} className={btnClass} title="Redo"><Redo size={14} /></button>
      {divider}
      <button onClick={() => exec('bold')} className={btnClass} title="Bold"><Bold size={14} /></button>
      <button onClick={() => exec('italic')} className={btnClass} title="Italic"><Italic size={14} /></button>
      <button onClick={() => exec('underline')} className={btnClass} title="Underline"><Underline size={14} /></button>
      <button onClick={() => exec('strikeThrough')} className={btnClass} title="Strikethrough"><Strikethrough size={14} /></button>
      {divider}
      <button onClick={() => exec('formatBlock', '<h1>')} className={btnClass} title="Heading 1"><Heading1 size={14} /></button>
      <button onClick={() => exec('formatBlock', '<h2>')} className={btnClass} title="Heading 2"><Heading2 size={14} /></button>
      <button onClick={() => exec('formatBlock', '<blockquote>')} className={btnClass} title="Quote"><Quote size={14} /></button>
      <button onClick={() => exec('formatBlock', '<pre>')} className={btnClass} title="Code block"><Code size={14} /></button>
      {divider}
      <button onClick={() => exec('insertUnorderedList')} className={btnClass} title="Bullet list"><List size={14} /></button>
      <button onClick={() => exec('insertOrderedList')} className={btnClass} title="Numbered list"><ListOrdered size={14} /></button>
      {divider}
      <button onClick={handleLink} className={btnClass} title="Insert link"><Link size={14} /></button>
      <button onClick={() => exec('insertHorizontalRule')} className={btnClass} title="Horizontal line"><Minus size={14} /></button>
      <button onClick={() => exec('removeFormat')} className={btnClass} title="Clear formatting"><RemoveFormatting size={14} /></button>
      {divider}
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttach} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className={btnClass}
        title="Attach file"
      >
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
      </button>
      {divider}
      {/* JPRO AI assist */}
      <div className="relative">
        <button
          ref={aiButtonRef}
          onClick={() => {
            saveSelection();
            if (!showAiMenu && aiButtonRef.current) {
              const rect = aiButtonRef.current.getBoundingClientRect();
              setMenuPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 208) });
            }
            setShowAiMenu(!showAiMenu);
          }}
          disabled={aiLoading}
          className={`${btnClass} ${showAiMenu ? (isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600') : ''}`}
          title="JPRO"
        >
          {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        </button>
        {showAiMenu && !aiLoading && ReactDOM.createPortal(
          <div ref={aiMenuRef} style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }} className={`w-52 rounded-xl shadow-xl border py-1 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-3 py-1.5 text-[10px] font-rasa font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              JPRO
            </div>
            <button
              onClick={() => handleAiAction('continue')}
              disabled={!hasContent()}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-rasa transition-colors disabled:opacity-30 ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <PenLine size={14} /> Continue writing
            </button>
            <button
              onClick={() => handleAiAction('summarize')}
              disabled={!hasContent()}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-rasa transition-colors disabled:opacity-30 ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <FileText size={14} /> Summarize note
            </button>

            {/* Improve submenu */}
            <div className="relative">
              <button
                onClick={() => setShowImproveMenu(!showImproveMenu)}
                disabled={!hasContent()}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-rasa transition-colors disabled:opacity-30 ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <span className="flex items-center gap-2"><Wand2 size={14} /> Improve text</span>
                <ChevronDown size={12} className={showImproveMenu ? 'rotate-180' : ''} />
              </button>
              {showImproveMenu && (
                <div className={`ml-2 mr-2 mb-1 rounded-lg ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
                  {[
                    { action: 'fix_grammar', label: 'Fix grammar & spelling' },
                    { action: 'make_professional', label: 'Make professional' },
                    { action: 'expand', label: 'Expand & add detail' },
                    { action: 'make_concise', label: 'Make concise' },
                  ].map(({ action, label }) => (
                    <button
                      key={action}
                      onClick={() => handleAiAction(action)}
                      className={`w-full text-left px-3 py-1.5 text-xs font-rasa transition-colors ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      {label}
                    </button>
                  ))}
                  <div className={`px-2 py-1.5 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <input
                      type="text"
                      placeholder="Custom instruction..."
                      className={`w-full px-2 py-1 text-xs font-rasa rounded border outline-none ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          handleAiAction('improve', e.target.value.trim());
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={`border-t my-1 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />

            {/* Generate from prompt */}
            {showPromptInput ? (
              <div className="px-2 py-1.5">
                <textarea
                  value={promptText}
                  onChange={(e) => {
                    setPromptText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  placeholder="Write about..."
                  autoFocus
                  rows={1}
                  className={`w-full px-2 py-1.5 text-xs font-rasa rounded border outline-none resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && promptText.trim()) {
                      e.preventDefault();
                      handleAiAction('generate', promptText.trim());
                      setPromptText('');
                    }
                    if (e.key === 'Escape') setShowPromptInput(false);
                  }}
                />
              </div>
            ) : (
              <button
                onClick={() => setShowPromptInput(true)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-rasa transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <MessageSquare size={14} /> Generate from prompt...
              </button>
            )}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

const DEFAULT_CATEGORIES = [
  'Meeting', 'Research', 'Ideas', 'Follow-up', 'Reference', 'Personal', 'Project', 'Training'
];

const COLOR_PALETTE = [
  // Row 1 — neutrals + basics
  '#FFFFFF', '#F3F4F6', '#E5E7EB', '#D1D5DB', '#9CA3AF',
  // Row 2 — warm
  '#FEF3C7', '#FDE68A', '#FCD34D', '#FBBF24', '#F59E0B',
  '#FED7AA', '#FDBA74', '#FB923C', '#F97316', '#EA580C',
  // Row 3 — reds/pinks
  '#FCE7F3', '#FBCFE8', '#F9A8D4', '#F472B6', '#EC4899',
  '#FEE2E2', '#FECACA', '#FCA5A5', '#F87171', '#EF4444',
  // Row 4 — blues/purples
  '#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6',
  '#E0E7FF', '#C7D2FE', '#A5B4FC', '#818CF8', '#6366F1',
  '#EDE9FE', '#DDD6FE', '#C4B5FD', '#A78BFA', '#8B5CF6',
  // Row 5 — greens/teals
  '#D1FAE5', '#A7F3D0', '#6EE7B7', '#34D399', '#10B981',
  '#CCFBF1', '#99F6E4', '#5EEAD4', '#2DD4BF', '#14B8A6',
];

function ensureHtml(text) {
  if (!text) return '';
  let html;
  if (/<[a-z][\s\S]*>/i.test(text)) {
    html = text;
  } else {
    try {
      html = marked(text, { breaks: true, gfm: true });
    } catch {
      html = text;
    }
  }
  // Resolve Active Storage URLs: convert redirect to proxy, resolve relative to full
  html = html.replace(/(src|href)="([^"]*\/rails\/active_storage\/[^"]+)"/g, (_, attr, url) => {
    // Convert redirect URLs to proxy URLs (proxy doesn't expire)
    let fixed = url.replace('/blobs/redirect/', '/blobs/proxy/');
    // Strip any existing host prefix to get relative path, then resolve
    const path = fixed.replace(/^https?:\/\/[^/]+/, '');
    return `${attr}="${resolveUrl(path)}"`;
  });
  return DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'data-attachment-id'] });
}

export default function NoteEditor({ note, onSave, onNoteCreated, onClose, onAutoTitle, onCollapseList, listOpen, onBack, theme }) {
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [color, setColor] = useState('#FFFFFF');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const editorRef = useRef(null);
  const categoryRef = useRef(null);

  // Content hydration — only on note switch (id change). Re-running this when the
  // same note's metadata changes (e.g., auto-title updates `title`) would wipe
  // unsaved inline images/edits because innerHTML gets replaced with note.content
  // which reflects the last-saved state.
  useEffect(() => {
    if (!editorRef.current) return;
    observerPausedRef.current = true;
    editorRef.current.innerHTML = note ? ensureHtml(note.content || '') : '';
    requestAnimationFrame(() => { observerPausedRef.current = false; });
  }, [note?.id]);

  // Metadata sync — title/category/color can safely update when the note prop changes
  // without losing in-editor state.
  useEffect(() => {
    if (note) {
      setTitle(note.title || '');
      const cats = (note.category || '').split(',').map(c => c.trim()).filter(Boolean);
      setCategories(cats);
      setColor(note.color || '#FFFFFF');
    } else {
      setTitle('');
      setCategories([]);
      setColor('#FFFFFF');
    }
  }, [note]);

  // Watch for deleted inline attachments — when user backspaces an image/file, clean up server-side
  // Use a ref to pause the observer during programmatic content changes (innerHTML =)
  // Watch for deleted inline attachments — only delete from server if truly removed
  // (not just moved/repositioned within the editor)
  const observerPausedRef = useRef(false);
  useEffect(() => {
    if (!editorRef.current || !note?.id) return;
    const observer = new MutationObserver((mutations) => {
      if (observerPausedRef.current) return;

      // Collect all removed attachment IDs
      const removedIds = new Set();
      for (const mutation of mutations) {
        for (const removed of mutation.removedNodes) {
          const el = removed.nodeType === 1 ? removed : null;
          if (!el) continue;
          const attachmentEls = el.dataset?.attachmentId
            ? [el]
            : [...(el.querySelectorAll?.('[data-attachment-id]') || [])];
          for (const att of attachmentEls) {
            if (att.dataset.attachmentId) removedIds.add(att.dataset.attachmentId);
          }
        }
      }

      if (removedIds.size === 0) return;

      // Wait a tick, then check if the attachment still exists in the editor
      // (drag/move removes then re-adds the node in the same mutation batch or next frame)
      setTimeout(() => {
        const editor = editorRef.current;
        if (!editor) return;
        for (const attId of removedIds) {
          const stillExists = editor.querySelector(`[data-attachment-id="${attId}"]`);
          if (!stillExists) {
            // Truly deleted — remove from server
            apiDelete(`/notes/${note.id}/attachments/${attId}`).catch(() => {});
          }
        }
      }, 300);
    });
    observer.observe(editorRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [note?.id]);

  // Image resize — click an image to select it, drag corners to resize
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let activeOverlay = null;

    const removeOverlay = () => {
      if (activeOverlay) {
        activeOverlay.remove();
        activeOverlay = null;
      }
    };

    const handleClick = (e) => {
      removeOverlay();
      const img = e.target.closest('img');
      if (!img || !editor.contains(img)) return;

      e.preventDefault();

      // Create resize overlay
      const overlay = document.createElement('div');
      overlay.style.cssText = `position:absolute;border:2px solid #3b82f6;pointer-events:none;z-index:10;`;
      const updateOverlay = () => {
        const rect = img.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();
        overlay.style.left = `${rect.left - editorRect.left + editor.scrollLeft}px`;
        overlay.style.top = `${rect.top - editorRect.top + editor.scrollTop}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;
      };
      updateOverlay();

      // Add corner handle
      const handle = document.createElement('div');
      handle.style.cssText = `position:absolute;right:-4px;bottom:-4px;width:10px;height:10px;background:#3b82f6;border-radius:2px;cursor:nwse-resize;pointer-events:all;`;
      overlay.appendChild(handle);

      // Make editor position relative for overlay
      editor.style.position = 'relative';
      editor.appendChild(overlay);
      activeOverlay = overlay;

      // Drag to resize
      const onMouseDown = (me) => {
        me.preventDefault();
        me.stopPropagation();
        const startX = me.clientX;
        const startWidth = img.offsetWidth;
        const aspectRatio = img.naturalWidth / img.naturalHeight || img.offsetWidth / img.offsetHeight;

        const onMouseMove = (mm) => {
          const dx = mm.clientX - startX;
          const newWidth = Math.max(50, startWidth + dx);
          img.style.width = `${newWidth}px`;
          img.style.height = `${newWidth / aspectRatio}px`;
          img.removeAttribute('data-original-width');
          updateOverlay();
        };

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      handle.addEventListener('mousedown', onMouseDown);
    };

    const handleClickOutside = (e) => {
      if (!e.target.closest('img') && activeOverlay) {
        removeOverlay();
      }
    };

    editor.addEventListener('click', handleClick);
    document.addEventListener('click', handleClickOutside);

    return () => {
      editor.removeEventListener('click', handleClick);
      document.removeEventListener('click', handleClickOutside);
      removeOverlay();
    };
  }, [note]);

  // Close pickers on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setShowCategoryPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const getContent = () => {
    return editorRef.current ? DOMPurify.sanitize(editorRef.current.innerHTML) : '';
  };

  const handleSave = async () => {
    const content = getContent();
    if (!content.trim() || content === '<br>') return;
    setSaving(true);
    await onSave({ title, content, category: categories.join(', '), color });
    setSaving(false);
  };

  const handleAutoTitle = async () => {
    if (note?.id) {
      await onAutoTitle(note.id);
    }
  };

  const handleCopy = async () => {
    if (!editorRef.current) return;
    try {
      // Copy as rich text (HTML) + plain text fallback
      const html = editorRef.current.innerHTML;
      const text = editorRef.current.innerText;
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback to plain text
      const text = editorRef.current.innerText;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const addCategory = (cat) => {
    const trimmed = cat.trim();
    if (trimmed && !categories.includes(trimmed)) {
      setCategories(prev => [...prev, trimmed]);
    }
    setCategoryInput('');
  };

  const removeCategory = (cat) => {
    setCategories(prev => prev.filter(c => c !== cat));
  };

  const handleCategoryKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addCategory(categoryInput);
    }
    if (e.key === 'Backspace' && !categoryInput && categories.length > 0) {
      setCategories(prev => prev.slice(0, -1));
    }
  };

  const isDark = theme === 'dark';

  return (
    <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Editor Header */}
      <div className={`flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-b flex-shrink-0 gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-1.5 md:gap-2 flex-1 min-w-0">
          {/* Mobile back button */}
          {onBack && (
            <button
              onClick={onBack}
              className={`md:hidden p-1 rounded transition-colors flex-shrink-0 ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
              title="Back to list"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          {/* Desktop collapse toggle */}
          {onCollapseList && (
            <button
              onClick={onCollapseList}
              className={`hidden md:block p-1 rounded transition-colors flex-shrink-0 ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
              title={listOpen ? 'Hide notes list' : 'Show notes list'}
            >
              {listOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Untitled note"
            className={`flex-1 bg-transparent outline-none text-lg font-unna font-medium min-w-0 ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
          />
          {note?.id && (
            <button
              onClick={handleAutoTitle}
              className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title="Generate title with AI"
            >
              <Wand2 size={16} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          {/* Color picker — hidden on mobile */}
          <div className="relative hidden md:block">
            <button
              onClick={() => { setShowColorPicker(!showColorPicker); setShowCategoryPicker(false); }}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Note color"
            >
              <div className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: color }} />
            </button>
            {showColorPicker && (
              <div className={`absolute right-0 top-full mt-1 p-3 rounded-xl shadow-xl border z-20 w-[220px] ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="grid grid-cols-5 gap-1.5">
                  {COLOR_PALETTE.map(c => (
                    <button
                      key={c}
                      onClick={() => { setColor(c); setShowColorPicker(false); }}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? 'border-blue-500 ring-2 ring-blue-300' : 'border-transparent hover:border-gray-400'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                {/* Custom color input */}
                <div className={`mt-2 pt-2 border-t flex items-center gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-7 h-7 rounded cursor-pointer border-0 p-0"
                  />
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className={`flex-1 px-2 py-1 text-xs rounded border font-mono ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'} outline-none`}
                    placeholder="#hex"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Category picker — hidden on mobile */}
          <div className="relative hidden md:block" ref={categoryRef}>
            <button
              onClick={() => { setShowCategoryPicker(!showCategoryPicker); setShowColorPicker(false); }}
              className={`flex items-center gap-1 px-2 py-1 text-xs font-rasa rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title="Categories"
            >
              <Tag size={12} />
              {categories.length > 0 ? `${categories.length} tag${categories.length > 1 ? 's' : ''}` : 'Tags'}
            </button>
            {showCategoryPicker && (
              <div className={`absolute right-0 top-full mt-1 p-3 rounded-xl shadow-xl border z-20 w-64 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                {/* Selected categories */}
                {categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {categories.map(cat => (
                      <span
                        key={cat}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-rasa ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}
                      >
                        {cat}
                        <button onClick={() => removeCategory(cat)} className="hover:text-red-400">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Custom input */}
                <div className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border mb-2 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                  <input
                    type="text"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyDown={handleCategoryKeyDown}
                    placeholder="Type to add..."
                    className={`flex-1 bg-transparent outline-none text-xs font-rasa ${isDark ? 'placeholder-gray-500' : 'placeholder-gray-400'}`}
                    autoFocus
                  />
                  {categoryInput && (
                    <button
                      onClick={() => addCategory(categoryInput)}
                      className="p-0.5 rounded text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                    >
                      <Plus size={12} />
                    </button>
                  )}
                </div>

                {/* Default suggestions */}
                <div className="flex flex-wrap gap-1">
                  {DEFAULT_CATEGORIES.filter(c => !categories.includes(c)).map(cat => (
                    <button
                      key={cat}
                      onClick={() => addCategory(cat)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-rasa transition-colors ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleCopy}
            className={`hidden md:flex p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'} ${copied ? 'text-green-500' : ''}`}
            title={copied ? 'Copied!' : 'Copy note'}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-rasa transition-colors"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Category tags bar — shown below header when tags exist */}
      {categories.length > 0 && (
        <div className={`flex items-center gap-1 px-4 py-1.5 border-b flex-shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <Tag size={11} className="text-gray-400 flex-shrink-0" />
          <div className="flex flex-wrap gap-1">
            {categories.map(cat => (
              <span
                key={cat}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-rasa ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
              >
                {cat}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Formatting Toolbar */}
      <FormatToolbar
        editorRef={editorRef}
        isDark={isDark}
        noteId={note?.id}
        onEnsureSaved={async () => {
          const content = getContent();
          if (!content.trim() || content === '<br>') return null;
          try {
            const response = await apiPost('/notes', { title, content, category: categories.join(', '), color });
            if (response.ok) {
              const data = await response.json();
              if (data.note && onNoteCreated) onNoteCreated(data.note);
              return data.note?.id;
            }
          } catch { /* silent */ }
          return null;
        }}
        onAiResult={(action, result, savedRange, selectionText) => {
          if (!editorRef.current || !result) return;
          observerPausedRef.current = true;
          const safeResult = DOMPurify.sanitize(result, { ADD_ATTR: ['target', 'data-attachment-id'] });

          const hasSelection = savedRange && selectionText && selectionText.trim().length > 0;

          // Helper: select all content then insert (undoable replacement)
          const replaceAll = (html) => {
            editorRef.current.focus();
            const sel = window.getSelection();
            sel.selectAllChildren(editorRef.current);
            document.execCommand('insertHTML', false, html);
          };

          if (action === 'generate') {
            replaceAll(safeResult);
          } else if (['improve', 'fix_grammar', 'make_professional', 'expand', 'make_concise'].includes(action)) {
            if (hasSelection) {
              editorRef.current.focus();
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(savedRange);
              document.execCommand('insertHTML', false, safeResult);
            } else {
              replaceAll(safeResult);
            }
          } else if (action === 'summarize') {
            if (hasSelection) {
              editorRef.current.focus();
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(savedRange);
              document.execCommand('insertHTML', false, safeResult);
            } else {
              editorRef.current.focus();
              const sel = window.getSelection();
              sel.selectAllChildren(editorRef.current);
              sel.collapseToEnd();
              document.execCommand('insertHTML', false, '<hr/>' + safeResult);
            }
          } else if (action === 'continue') {
            if (hasSelection) {
              editorRef.current.focus();
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(savedRange);
              sel.collapseToEnd();
              document.execCommand('insertHTML', false, safeResult);
            } else {
              editorRef.current.focus();
              const sel = window.getSelection();
              sel.selectAllChildren(editorRef.current);
              sel.collapseToEnd();
              document.execCommand('insertHTML', false, safeResult);
            }
          }

          requestAnimationFrame(() => { observerPausedRef.current = false; });
        }}
      />

      {/* Rich text editor body */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Start writing..."
        className={`flex-1 min-h-0 px-4 md:px-8 py-4 md:py-6 outline-none text-base leading-relaxed overflow-y-auto overflow-x-auto font-rasa prose prose-base max-w-none break-words [&_table]:block [&_table]:overflow-x-auto [&_table]:w-max [&_table]:min-w-full ${
          isDark ? 'bg-gray-900 prose-invert' : 'bg-white'
        } [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-400 [&:empty]:before:pointer-events-none`}
      />
    </div>
  );
}
